import {Fragment, useState} from 'react'
import {Link, useParams} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {PageHeading} from '@/components/page-heading'
import {HelpIcon} from '@/components/help-icon'
import {PorQue} from '@/components/ui/por-que'
import {TrazabilidadSwitch, useTrazabilidad} from '@/components/perfil/provenance-note'
import {PreguntaContradiccionModal} from '@/components/pregunta-contradiccion-modal'
import {PreguntaResponderModal} from '@/components/pregunta-responder-modal'
import {Button} from '@/components/ui/button'
import {PaginationBar} from '@/components/ui/pagination-bar'
import {Skeleton} from '@/components/ui/skeleton'
import {Tag} from '@/components/ui/tag'
import {useOperatorPage} from '@/components/no-access-state'
import {useCapabilities} from '@/hooks/empresa/organizacion/use-businesses'
import {useGodModeReadOnly} from '@/hooks/platform/use-god-mode'
import {useTableState} from '@/hooks/common/use-table-state'
import {
  PREGUNTAS_PAGE_SIZE,
  usePreguntasCargo,
  usePreguntasLista,
  type EstadoPregunta as EstadoDeLaLista,
  type PreguntaCargo,
} from '@/hooks/empresa/perfil_cargo/use-preguntas-cargo'
import {translateApiError} from '@/lib/errors'
import {formatDate} from '@/lib/risk'
import {SearchInput} from '@/components/ui/search-input'

/** «Todavía no sé» deja sin definir el requisito que depende de ella: ámbar, nunca verde. */
const TONOS = {respondida: 'green', no_aplica: 'neutral', no_se: 'amber'} as const

function EstadoPregunta({pregunta}: {pregunta: PreguntaCargo}) {
  const {t} = useTranslation()
  if (!pregunta.respuesta_tipo) {
    return (
      <Tag tone="neutral" data-testid={`pregunta-estado-${pregunta.pregunta_key}`}>
        {t('preguntaCargo.estado.sinContestar')}
      </Tag>
    )
  }
  return (
    <Tag tone={TONOS[pregunta.respuesta_tipo]} data-testid={`pregunta-estado-${pregunta.pregunta_key}`}>
      {t(`preguntaCargo.tipos.${pregunta.respuesta_tipo}`)}
    </Tag>
  )
}

/** Encendida se imprime en línea: la constancia sale en la carpeta de la visita y un popover cerrado no está en el DOM.
 * Sin autor la contestó un operador en modo dios: la columna es nullable porque no hay identidad de cliente. */
function Traza({pregunta, businessId, desplegado}: {pregunta: PreguntaCargo; businessId: string; desplegado: boolean}) {
  const {t} = useTranslation()
  const texto = pregunta.respuesta ?? pregunta.no_aplica_motivo
  const fecha = pregunta.respondida_en ? formatDate(pregunta.respondida_en) : ''
  if (!pregunta.fila_numero && !pregunta.respuesta_tipo) return null
  return (
    <div className="space-y-1">
      {pregunta.respuesta_tipo && texto && (
        <p className="text-xs text-muted-foreground" data-testid={`pregunta-respuesta-${pregunta.pregunta_key}`}>
          «{texto}»
        </p>
      )}
      {desplegado && (
        <PorQue
          label={t('common.deDondeSalio')}
          titulo={t('common.deDondeSalio')}
          ariaLabel={`${t('common.deDondeSalio')} — ${pregunta.enunciado}`}
          contenidoTestId={`pregunta-traza-contenido-${pregunta.pregunta_key}`}
          siempreVisible>
          {!!pregunta.fila_numero && (
            <p data-testid={`pregunta-fila-contenido-${pregunta.pregunta_key}`}>
              {t('preguntaCargo.deLaFila', {fila: pregunta.fila_numero})}{' '}
              <Link className="underline" to={`/businesses/${businessId}/sgsst/matriz-riesgos`}>
                {t('preguntaCargo.verLaFila')}
              </Link>
            </p>
          )}
          {pregunta.respuesta_tipo && (
            <p data-testid={`pregunta-constancia-contenido-${pregunta.pregunta_key}`}>
              {pregunta.respondida_por_nombre
                ? t('preguntaCargo.constancia', {nombre: pregunta.respondida_por_nombre, fecha})
                : t('preguntaCargo.constanciaPlataforma', {fecha})}
              {!!pregunta.desbloqueo_constancia &&
                t('preguntaCargo.constanciaDesbloqueo', {count: pregunta.desbloqueo_constancia})}
            </p>
          )}
        </PorQue>
      )}
    </div>
  )
}

function Desbloquea({pregunta}: {pregunta: PreguntaCargo}) {
  const {t} = useTranslation()
  return (
    <p className="text-xs text-muted-foreground" data-testid={`pregunta-desbloquea-${pregunta.pregunta_key}`}>
      {pregunta.requisitos.length
        ? t('preguntaCargo.desbloquea', {requisitos: pregunta.requisitos.join(', ')})
        : t('preguntaCargo.sinRequisito')}
    </p>
  )
}

function Choque({pregunta}: {pregunta: PreguntaCargo}) {
  const {t} = useTranslation()
  return (
    <div className="notice-amber rounded-lg px-3 py-2 text-xs" data-testid={`pregunta-choque-${pregunta.pregunta_key}`}>
      <p>
        {t('preguntaCargo.choque.aviso', {
          fila: pregunta.contradice_fila_numero,
          descripcion: pregunta.contradice_fila_descripcion,
        })}
      </p>
      {pregunta.decision_contradiccion ? (
        <p data-testid={`pregunta-choque-decidido-${pregunta.pregunta_key}`}>
          {t(`preguntaCargo.choque.decidido.${pregunta.decision_contradiccion}`, {
            nombre: pregunta.decidido_por_nombre ?? '',
            fecha: pregunta.decidido_en ? formatDate(pregunta.decidido_en) : '',
            motivo: pregunta.decision_contradiccion_motivo ?? '',
          })}
        </p>
      ) : (
        <p>{t('preguntaCargo.choque.pendiente')}</p>
      )}
    </div>
  )
}

type FilaProps = {
  businessId: string
  puedeEditar: boolean
  desplegado: boolean
  onResponder: (pregunta: PreguntaCargo) => void
  onContradiccion: (pregunta: PreguntaCargo) => void
}

function FilaPregunta({
  pregunta,
  businessId,
  puedeEditar,
  desplegado,
  onResponder,
  onContradiccion,
  rotulo,
}: FilaProps & {pregunta: PreguntaCargo; rotulo?: string}) {
  const {t} = useTranslation()
  const enChoque = pregunta.contradice_riesgo_fila_id !== null
  return (
    <div
      className="flex flex-wrap items-start justify-between gap-3 px-4 py-3.5"
      data-testid={`pregunta-${pregunta.pregunta_key}`}>
      <div className="min-w-0 space-y-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-medium">{pregunta.enunciado}</span>
          <EstadoPregunta pregunta={pregunta} />
        </div>
        <Desbloquea pregunta={pregunta} />
        <Traza pregunta={pregunta} businessId={businessId} desplegado={desplegado} />
        {enChoque && <Choque pregunta={pregunta} />}
      </div>
      {/* «Corregir» nunca desaparece: el choque más frecuente es un dedazo, y salir de él no debe exigir firmante. */}
      <div className="flex flex-wrap gap-2">
        <Button
          size="sm"
          variant="outline"
          disabled={!puedeEditar}
          data-testid={`pregunta-responder-${pregunta.pregunta_key}`}
          onClick={() => onResponder(pregunta)}>
          {rotulo ?? (pregunta.respuesta_tipo ? t('preguntaCargo.corregir') : t('preguntaCargo.responder'))}
        </Button>
        {enChoque && (
          <Button
            size="sm"
            variant="outline"
            data-testid={`pregunta-decidir-${pregunta.pregunta_key}`}
            onClick={() => onContradiccion(pregunta)}>
            {pregunta.decision_contradiccion ? t('preguntaCargo.cambiarDecision') : t('preguntaCargo.decidir')}
          </Button>
        )}
      </div>
    </div>
  )
}

/** Se puede contestar igual: el descarte sale de un match de subcadena sobre texto libre y la respuesta humana gana. */
function Descartada({pregunta, ...filaProps}: FilaProps & {pregunta: PreguntaCargo}) {
  const {t} = useTranslation()
  return (
    <div data-testid={`pregunta-descartada-${pregunta.pregunta_key}`}>
      <p className="px-4 pt-3 text-xs text-muted-foreground">
        {t('preguntaCargo.descartada', {fila: pregunta.fila_numero})}
      </p>
      <FilaPregunta pregunta={pregunta} rotulo={t('preguntaCargo.responderIgual')} {...filaProps} />
    </div>
  )
}

function Seccion({titulo, testId, children}: {titulo?: string; testId: string; children: React.ReactNode}) {
  return (
    <section className="space-y-2" data-testid={testId}>
      {titulo && <h2 className="font-mono text-xs tracking-wider text-muted-foreground uppercase">{titulo}</h2>}
      <div className="surface divide-y divide-border rounded-xl">{children}</div>
    </section>
  )
}

/** `render` y no `children`: «Descartadas» envuelve distinto su fila sin duplicar la sección, que el gate de higiene bloquearía. */
function SeccionPaginada({
  businessId,
  positionId,
  estado,
  titulo,
  testId,
  render,
}: {
  businessId: string | undefined
  positionId: string | undefined
  estado: EstadoDeLaLista
  titulo?: string
  testId: string
  render: (pregunta: PreguntaCargo) => React.ReactNode
}) {
  const {t} = useTranslation()
  const table = useTableState('enunciado')
  const lista = usePreguntasLista(businessId, positionId, estado, table.page, table.search)
  if (!lista.data) {
    return (
      <Seccion titulo={titulo} testId={testId}>
        <Skeleton className="h-24 w-full" />
      </Seccion>
    )
  }
  return (
    <Seccion titulo={titulo} testId={testId}>
      <div className="p-3">
        <SearchInput
          value={table.searchInput}
          onChange={table.setSearchInput}
          placeholder={t('common.search')}
          className="max-w-sm"
        />
      </div>
      {lista.data.items.length === 0 && (
        <p className="px-4 py-3 text-sm text-muted-foreground" data-testid={`preguntas-vacio-${estado}`}>
          {t('preguntaCargo.ninguna')}
        </p>
      )}
      {lista.data.items.map((pregunta) => (
        <Fragment key={pregunta.pregunta_key}>{render(pregunta)}</Fragment>
      ))}
      <div className="px-4 py-2">
        <PaginationBar
          page={table.page}
          pageSize={PREGUNTAS_PAGE_SIZE}
          total={lista.data.total}
          onPageChange={table.setPage}
        />
      </div>
    </Seccion>
  )
}

function SinSenal({titulares, filas}: {titulares: number; filas: number}) {
  const {t} = useTranslation()
  if (filas > 0) return null
  return (
    <div className="space-y-1 text-xs text-muted-foreground" data-testid="preguntas-sin-senal">
      <p>{titulares === 0 ? t('preguntaCargo.sinTitulares') : t('preguntaCargo.sinFilas')}</p>
      <PorQue label={t('common.porQue')} testId="preguntas-sin-senal-porque">
        <p>{titulares === 0 ? t('preguntaCargo.sinTitularesPorque') : t('preguntaCargo.sinFilasPorque')}</p>
      </PorQue>
    </div>
  )
}

/** El tope de preguntas es lo que Kairos pide, no un techo: la tanda abre las demás (E10-H04). */
export function PreguntasCargoPage() {
  const {t} = useTranslation()
  const {businessId, positionId} = useParams()
  const {business, guard} = useOperatorPage(businessId)
  const capabilities = useCapabilities(businessId)
  const godReadOnly = useGodModeReadOnly()
  const preguntas = usePreguntasCargo(business ? businessId : undefined, positionId)
  const [respondiendo, setRespondiendo] = useState<PreguntaCargo | null>(null)
  const [decidiendo, setDecidiendo] = useState<PreguntaCargo | null>(null)
  const [tandaAbierta, setTandaAbierta] = useState(false)
  const [trazabilidad, setTrazabilidad] = useTrazabilidad()

  if (guard) return guard
  if (preguntas.isError) {
    return (
      <div className="space-y-3">
        <PageHeading title={t('preguntaCargo.title')} subtitle={business!.name} />
        <p className="notice-amber max-w-2xl rounded-lg px-3 py-2 text-sm" data-testid="preguntas-error-carga">
          {translateApiError(preguntas.error)}
        </p>
        <Button size="sm" variant="outline" onClick={() => void preguntas.refetch()}>
          {t('perfilCargo.reintentar')}
        </Button>
      </div>
    )
  }
  const data = preguntas.data
  if (!data) {
    return (
      <div className="space-y-3">
        <PageHeading title={t('preguntaCargo.title')} subtitle={business!.name} />
        <Skeleton className="h-96 w-full" data-testid="preguntas-cargando" />
      </div>
    )
  }

  const puedeEditar = capabilities.sgsst_operar && !godReadOnly
  const puedeFirmar = capabilities.sgsst_firmar && !godReadOnly
  const razonBloqueo = godReadOnly ? t('preguntaCargo.bloqueoModoDios') : capabilities.firmar_bloqueado_por
  const filaProps = {
    businessId: businessId!,
    puedeEditar,
    desplegado: trazabilidad,
    onResponder: setRespondiendo,
    onContradiccion: setDecidiendo,
  }
  const todasContestadas = data.pedidas.length > 0 && data.pedidas.every((pregunta) => pregunta.respuesta_tipo)

  return (
    <div className="space-y-5" data-testid="preguntas-cargo">
      <Link
        className="text-sm underline"
        data-testid="preguntas-volver"
        to={`/businesses/${businessId}/sgsst/perfiles-cargo/${positionId}`}>
        {t('preguntaCargo.volverAlPerfil')}
      </Link>
      <PageHeading
        title={t('preguntaCargo.title')}
        subtitle={data.position_nombre}
        ayuda={
          <HelpIcon label={t('common.help.iconLabelFor', {campo: t('preguntaCargo.title')})} testId="preguntas-intro">
            <p>{t('preguntaCargo.intro', {tope: data.tope_pedidas})}</p>
            {data.catalogo_semilla_minima && <p className="mt-1.5">{t('preguntaCargo.semilla')}</p>}
          </HelpIcon>
        }
      />
      {!puedeEditar && razonBloqueo && (
        <p className="notice-amber max-w-2xl rounded-lg px-3 py-2 text-sm" data-testid="preguntas-bloqueo">
          {razonBloqueo}
        </p>
      )}
      <div className="flex justify-end">
        <TrazabilidadSwitch desplegado={trazabilidad} onChange={setTrazabilidad} testId="preguntas-trazabilidad" />
      </div>
      <SinSenal titulares={data.titulares} filas={data.filas_del_cargo} />

      {/* `pedidas` no se pagina: son tres por diseño, el grupo corto que R6 exceptúa. */}
      <Seccion titulo={t('preguntaCargo.seccion.pedidas')} testId="preguntas-pedidas">
        {data.pedidas.map((pregunta) => (
          <FilaPregunta key={pregunta.pregunta_key} pregunta={pregunta} {...filaProps} />
        ))}
      </Seccion>

      {todasContestadas && (
        <p className="text-sm text-muted-foreground" data-testid="preguntas-listo">
          {t('preguntaCargo.listo', {tope: data.tope_pedidas})}
        </p>
      )}

      {data.pendientes_total > 0 && (
        <section className="space-y-2" data-testid="preguntas-tanda">
          <Button
            size="sm"
            variant="outline"
            data-testid="preguntas-abrir-tanda"
            onClick={() => setTandaAbierta((abierta) => !abierta)}>
            {t('preguntaCargo.tanda', {count: data.pendientes_total})}
            {data.pendientes_desbloquean > 0
              ? t('preguntaCargo.tandaDesbloquea', {count: data.pendientes_desbloquean})
              : t('preguntaCargo.tandaOpcional')}
          </Button>
          {data.mucha_incertidumbre && data.filas_sin_valorar > 0 && (
            <div className="notice-amber space-y-1 rounded-lg px-3 py-2 text-sm" data-testid="preguntas-incertidumbre">
              <p>{t('preguntaCargo.muchaIncertidumbre', {count: data.filas_sin_valorar})}</p>
              <PorQue label={t('common.porQue')} testId="preguntas-incertidumbre-porque">
                <p>{t('preguntaCargo.muchaIncertidumbrePorque')}</p>
              </PorQue>
            </div>
          )}
          {tandaAbierta && (
            <div data-testid="preguntas-pendientes">
              <SeccionPaginada
                businessId={businessId}
                positionId={positionId}
                estado="pendiente"
                testId="preguntas-pendientes-lista"
                render={(pregunta) => <FilaPregunta pregunta={pregunta} {...filaProps} />}
              />
            </div>
          )}
        </section>
      )}

      {/* Con cero en la cabecera no se pide la lista, cuya derivación es costosa; la sección igual se pinta vacía. */}
      {data.empresa_total > 0 ? (
        <SeccionPaginada
          businessId={businessId}
          positionId={positionId}
          estado="empresa"
          titulo={t('preguntaCargo.seccion.empresa')}
          testId="preguntas-empresa"
          render={(pregunta) => <FilaPregunta pregunta={pregunta} {...filaProps} />}
        />
      ) : (
        <Seccion titulo={t('preguntaCargo.seccion.empresa')} testId="preguntas-empresa">
          <p className="px-4 py-3 text-sm text-muted-foreground" data-testid="preguntas-empresa-vacio">
            {t('preguntaCargo.ninguna')}
          </p>
        </Seccion>
      )}

      {data.descartadas_total > 0 && (
        <SeccionPaginada
          businessId={businessId}
          positionId={positionId}
          estado="descartada"
          titulo={t('preguntaCargo.seccion.descartadas')}
          testId="preguntas-descartadas"
          render={(pregunta) => <Descartada pregunta={pregunta} {...filaProps} />}
        />
      )}

      <PorQue
        label={t('preguntaCargo.noSePreguntaLabel')}
        testId="preguntas-no-se-pregunta"
        contenidoTestId="preguntas-no-se-pregunta-contenido">
        <p>{t('preguntaCargo.noSePregunta', {lista: data.no_se_pregunta.join('; ')})}</p>
      </PorQue>

      <PreguntaResponderModal
        open={respondiendo !== null}
        onOpenChange={(abierto) => !abierto && setRespondiendo(null)}
        businessId={businessId!}
        positionId={positionId!}
        pregunta={respondiendo}
      />
      <PreguntaContradiccionModal
        open={decidiendo !== null}
        onOpenChange={(abierto) => !abierto && setDecidiendo(null)}
        businessId={businessId!}
        positionId={positionId!}
        pregunta={decidiendo}
        puedeFirmar={puedeFirmar}
        razonBloqueo={razonBloqueo}
      />
    </div>
  )
}
